import { getWallStreetOrientation } from "building-common/walls/wall";
import type { Block } from "building-types/blocks/blockTypes";
import { StreetOrientationMode } from "building-types/building/streetOrientationMode";
import type { Facade, WallFacadeTable } from "building-types/facades/facadeTypes";
import type { Label } from "building-types/facades/label";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { roomEntityHandleForSelectingReportingUnit } from "building-types/handles/roomEntityHandleForSelectingReportingUnit";
import type { Level } from "building-types/levels/levelTypes";
import type {
  HalfEdgeAssociationTable,
  LevelHalfEdgeAssociationTable,
} from "building-types/planarGraphs/planarGraphAssociationTypes";
import type { PlanarGraphPerimeterInfo } from "building-types/planarGraphs/planarGraphPerimeterInfo";
import { DEFAULT_CORNER_TRIM_PROFILE_DESCRIPTOR } from "building-types/profiles/defaultBoardTrimProfiles";
import { ProfileExtrusionType } from "building-types/profiles/profileTypesEnum";
import type { GeneratedRoofSystem, UnderRoofTrim } from "building-types/roofs/generatedRoofSystemTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { ExteriorTrimSettings } from "building-types/settings/exteriorTrimSettings";
import type { ActiveProfileSource } from "building-types/settings/profileCatalogSettings";
import { RoofSide } from "building-types/settings/roofTypes";
import { CornerBoardTrimAlignment } from "building-types/settings/settingsTypesEnum";
import { ExteriorTrimViewNoteType } from "building-types/settings/viewNotesSettings";
import type { WainscotingSettings } from "building-types/settings/wainscotingSettings";
import type { WallSettings } from "building-types/settings/wallSettings";
import type {
  CornerBoardConfig,
  CornerBoardOffsetParams,
  CornerBoardProfileOptions,
} from "building-types/trim/cornerBoardTypes";
import { ValidCornerCondition, type CornerBoardRail, type CornerBoards } from "building-types/trim/cornerBoardTypes";
import type { FaceArrayViewEntity } from "building-types/views/viewEntity";
import { ViewEntityType } from "building-types/views/viewEntityType";
import { WallCladdingType } from "building-types/walls/wallDesciptorTypes";
import { WallType } from "building-types/walls/wallEnums";
import { WallLayerType } from "building-types/walls/wallEnums";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { WallMass } from "building-types/walls/wallTypes";
import { WallLOD } from "building-types/walls/wallTypes";
import { GABLE_ROOF_THICKNESS, ROOF_THICKNESS } from "common/constants/buildingObjects";
import type { RWTable, Table } from "common/data/table";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { radianToDegree } from "common/utils/math";
import uid from "common/utils/uid";
import { isWalledRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { defaultAllExteriorTrimViewNotes } from "doc/settings/defaultViewNoteSettings";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import { viewEntityDescriptorNew } from "doc/utils/viewEntityTools";
import { mergeViewNoteTables } from "doc/utils/viewNotesSettingsTools";
import { getExteriorWallSubstrateOffset } from "doc/utils/wallTypeTools";
import { aabb2Grow } from "geom/aabb2";
import { aabb2Height, aabb2New } from "geom/aabb2Types";
import { aabb2Width } from "geom/aabb2Types";
import type { AABB2 } from "geom/aabb2Types";
import type { Face } from "geom/brep";
import { isPointInsideBSP } from "geom/bsp";
import type { ClipRailContext } from "geom/bspClipping";
import { clipRails3, clippingNodeNew } from "geom/bspClipping";
import type { NodeBSP } from "geom/bspTypes";
import { Vec2PolyLine2ContainmentClass, classifyContainmentVec2PolyLine2 } from "geom/containment";
import { DIST_EPSILON, ONE_MINUS_DIST_EPSILON, ZERO_EPSILON } from "geom/core";
import { line2Tangent } from "geom/line2";
import { offsetPolyLine2Variable, polyLine2MiterPt } from "geom/offset";
import { planarGraphHalfEdgeEnd, planarGraphHalfEdgeStart, planarGraphTracePerimeter } from "geom/planarGraph";
import { planeNormal, planeProject } from "geom/plane";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import {
  polyLine2AABB2,
  polyLine2Copy,
  polyLine2End,
  polyLine2Move,
  polyLine2Simplify,
  polyLine2Start,
} from "geom/polyLine2";
import { polyLine2New } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import { polyLine3ByTwoPoints, polyLine3EndPoint } from "geom/polyLine3";
import { vec2AngleBetween, vec2Dist } from "geom/vec2";
import { vec3Dot, vec3Lerp, vec3Sub } from "geom/vec3";
import RBush from "rbush";
import { pushMissingProfileReferenceIssue } from "../../elements/profileMissingReference";
import { getActiveProfileDescriptor, getProfileExtrusionDescriptorProfile } from "../../elements/profileTools";
import { getEaveDepth } from "../../elements/roofTrimUtility";
import { iterateFacadesForHalfEdge } from "../../utils/facade";
import { getPlanarGraphFaces } from "../../utils/planarGraphPerimeters";
import { buildSubRoofHalfStoryDiscardBSP } from "../walls/halfStories";
import { getWallPath, wallHasNonInteriorRoomEnclosure } from "../walls/wall";
import { solidWallSubstrateLayer, wallMassLayers } from "../walls/wallLayers";
import {
  getCornerCondition,
  isValidCornerCondition,
  cornerBoardConfigForEdge,
  resolveCornerBoards,
  mergeCornerBoardConfig,
  cornerBoardsNew,
  ROOF_CORNER_BOARD_TOLERANCE,
  buildCornerBoardProfile,
  getCornerBoardRefPointFromOffsets,
  getCornerBoardRefPoint,
  CORNER_BOARD_COLLISION_TOLERANCE,
} from "./cornerBoardTools";

interface CornerBoardBehaviorBox extends AABB2, CornerBoardConfig {}

interface BuildCornerBoardsOptions {
  readonly levels: Table<Level>;
  readonly blocks: Table<Block>;
  readonly labels: LevelHalfEdgeAssociationTable<Label>;
  readonly planarGraphs: Table<PolygonPlanarGraph>;
  readonly planarGraphPerimeters: Table<PlanarGraphPerimeterInfo>;
  readonly roofs: Table<GeneratedRoofSystem>;
  readonly rooms: Table<Room>;
  readonly roomLevelIndices: Table<number>;
  readonly roomsByLevel: Table<string[]>;
  readonly facades: Table<Facade>;
  readonly wallSystems: Table<WallSystem>;
  readonly exteriorTrimSettings: ExteriorTrimSettings;
  readonly profileSource: ActiveProfileSource;
  readonly wainscotingSettings: WainscotingSettings;
  readonly wallSettings: WallSettings;
  readonly issues?: BuildingGenerationIssue[];
}

export function makeCornerBoards({
  levels,
  blocks,
  labels,
  planarGraphs,
  planarGraphPerimeters,
  roofs,
  rooms,
  roomLevelIndices,
  roomsByLevel,
  facades,
  wallSystems,
  exteriorTrimSettings,
  profileSource,
  wainscotingSettings,
  wallSettings,
  issues,
}: BuildCornerBoardsOptions): Table<CornerBoards> {
  const cornerBoards: RWTable<CornerBoards> = {};

  // A stack is a group of corner boards at roughly the same location.
  const cornerBoardStacks: CornerBoardProfileOptions[][] = [];

  // This RTree is used to map roof wall corners to room wall corners and make the
  // roof wall corners to inherit the behavior from the room wall corner below.
  const cornerBoardBehaviorRTree = new RBush<CornerBoardBehaviorBox>();

  const exteriorWallOffsetWithSheathing = getExteriorWallSubstrateOffset(wallSettings);

  const roofSystemsCornerBoardClipping: NodeBSP[] = [];
  for (const roofId in roofs) {
    const roof = roofs[roofId];

    const { roofs: subRoofs } = roof;
    for (const subRoofId in subRoofs) {
      const subRoof = subRoofs[subRoofId];
      const discardBSP = buildSubRoofHalfStoryDiscardBSP(subRoof);
      if (!discardBSP) {
        continue;
      }
      roofSystemsCornerBoardClipping.push(discardBSP);
    }
  }

  const clipCornerBoardOptions: ClipRailContext = {
    clippingNodes: roofSystemsCornerBoardClipping.map((x) => clippingNodeNew(x)),
    coplanarsToFront: false,
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  const addCornerBoardSegment = (options: CornerBoardProfileOptions, stackIndex: number) => {
    if (stackIndex >= cornerBoardStacks.length) {
      cornerBoardStacks.push([]);
    }
    cornerBoardStacks[stackIndex].push(options);
  };

  const addCornerBoardSegments = (
    options: CornerBoardProfileOptions,
    offsets1: CornerBoardOffsetParams,
    offsets2: CornerBoardOffsetParams,
    index: number,
  ) => {
    if (options.hasBoardEdge1 && options.hasBoardEdge2 && hasDifferentOffsets(offsets1, offsets2)) {
      addCornerBoardSegment({ ...options, hasBoardEdge2: false, ...offsets1 }, index);
      addCornerBoardSegment({ ...options, hasBoardEdge1: false, ...offsets2 }, index);
    } else if (options.hasBoardEdge1) {
      addCornerBoardSegment({ ...options, ...offsets1 }, index);
    } else if (options.hasBoardEdge2) {
      addCornerBoardSegment({ ...options, ...offsets2 }, index);
    }
  };

  const {
    enableOutsideCornerMoulding,
    enableInsideCornerMoulding,
    outsideCornerBoardProfile = ProfileExtrusionType.DEFAULT_PROFILE,
    insideCornerBoardProfile = ProfileExtrusionType.DEFAULT_PROFILE,
    outsideCornerBoardMaterial,
    insideCornerBoardMaterial,
    outsideCornerBoardBottomAlignment = CornerBoardTrimAlignment.TO_FOUNDATION,
    outsideCornerBoardTopAlignment = CornerBoardTrimAlignment.BO_SOFFIT,
    outsideCornerBoardBottomOffset = 0,
    outsideCornerBoardTopOffset = 0,
  } = exteriorTrimSettings;

  const exteriorTrimViewNoteTable = mergeViewNoteTables(
    defaultAllExteriorTrimViewNotes,
    exteriorTrimSettings.viewNotes,
  );

  const outsideDescriptor = getActiveProfileDescriptor(
    profileSource,
    outsideCornerBoardProfile,
    DEFAULT_CORNER_TRIM_PROFILE_DESCRIPTOR,
  );
  const exteriorTrimHandle: DocEntityHandle | undefined = exteriorTrimSettings.id
    ? { type: "SETTINGS" as const, id: exteriorTrimSettings.id }
    : undefined;
  if (!outsideDescriptor) {
    pushMissingProfileReferenceIssue(
      issues,
      profileSource,
      outsideCornerBoardProfile,
      exteriorTrimHandle,
      "outsideCornerBoardProfile",
    );
  }
  const outsideProfile = polyLine2Simplify(polyLine2Copy(getProfileExtrusionDescriptorProfile(outsideDescriptor)));
  const outsideProfileAABB = polyLine2AABB2(outsideProfile);
  const outsideThickness = aabb2Width(outsideProfileAABB);
  const outsideWidth = aabb2Height(outsideProfileAABB);
  const outsideViewEntityDescriptor = viewEntityDescriptorNew(
    uid(),
    ViewEntityType.OUTSIDE_CORNER,
    exteriorTrimViewNoteTable[ExteriorTrimViewNoteType.OUTSIDE_CORNER],
  );

  polyLine2Move(outsideProfile, -outsideProfileAABB.maxX, -outsideProfileAABB.minY);

  const insideDescriptor = getActiveProfileDescriptor(
    profileSource,
    insideCornerBoardProfile,
    DEFAULT_CORNER_TRIM_PROFILE_DESCRIPTOR,
  );
  if (!insideDescriptor) {
    pushMissingProfileReferenceIssue(
      issues,
      profileSource,
      insideCornerBoardProfile,
      exteriorTrimHandle,
      "insideCornerBoardProfile",
    );
  }
  const insideProfile = polyLine2Simplify(polyLine2Copy(getProfileExtrusionDescriptorProfile(insideDescriptor)));
  const insideProfileAABB = polyLine2AABB2(insideProfile);
  const insideThickness = aabb2Width(insideProfileAABB);
  const insideWidth = aabb2Height(insideProfileAABB);
  const insideViewEntityDescriptor = viewEntityDescriptorNew(
    uid(),
    ViewEntityType.INSIDE_CORNER,
    exteriorTrimViewNoteTable[ExteriorTrimViewNoteType.INSIDE_CORNER],
  );

  const hasProfileForCornerCondition = (cornerCondition: ValidCornerCondition): boolean =>
    cornerCondition === ValidCornerCondition.INSIDE ? !!insideDescriptor : !!outsideDescriptor;

  polyLine2Move(insideProfile, -insideProfileAABB.maxX, -insideProfileAABB.minY);

  const outsideDist = -outsideWidth + 3;
  const insideDist = -insideWidth + 3;

  const roofFaces = collectRoofFaces(roofs);
  const underRoofTrim = collectUnderRoofTrim(roofs);
  const isUnderRoofTrimInserted: { [key in number]?: boolean } = {};

  // corner board
  for (const i in planarGraphs) {
    const iIsInLevels = Object.keys(levels).includes(i);
    if (!iIsInLevels) {
      continue;
    }
    const planarGraph = planarGraphs[i].planarGraph;
    const { halfEdgeToWall, walls } = wallSystems[i];

    const level = levels[i];
    const { floorElevation, topOfWall, floorIndex } = level;

    const perimeterInfo = planarGraphPerimeters[i];

    const { height: wainscotingHeight, wainscotingMode } = wainscotingSettings;

    const adjustForWainscoting =
      floorIndex === 0 && wainscotingHeight > 0.0 && wainscotingMode === StreetOrientationMode.ALL_FACES;

    let faceHalfEdges: number[][];

    if (floorIndex < 0) {
      continue;
    } else if (floorIndex === 0) {
      const perimeterFacesToExclude = getPlanarGraphFaces(
        rooms,
        roomLevelIndices,
        roomsByLevel[i],
        planarGraphs[i],
        (room) => {
          return !room.attributes.hasExteriorTrim;
        },
      );

      const perim = planarGraphTracePerimeter(planarGraph, perimeterFacesToExclude);
      faceHalfEdges = perim.faceHalfEdges;
    } else {
      const perimeterFacesToExclude1 = getPlanarGraphFaces(
        rooms,
        roomLevelIndices,
        roomsByLevel[i],
        planarGraphs[i],
        (room) => {
          if (!room.attributes.hasExteriorTrim) {
            return true;
          }
          const block = blocks[room.parentId];
          return !!block.isHalfStory;
        },
      );

      const perimeterFacesToExclude2 = getPlanarGraphFaces(
        rooms,
        roomLevelIndices,
        roomsByLevel[i],
        planarGraphs[i],
        (room) => {
          if (!room.attributes.hasExteriorTrim) {
            return true;
          }
          const block = blocks[room.parentId];
          return !block.isHalfStory;
        },
      );

      const perim1 = planarGraphTracePerimeter(planarGraph, perimeterFacesToExclude1);
      const perim2 = planarGraphTracePerimeter(planarGraph, perimeterFacesToExclude2);

      faceHalfEdges = perim1.faceHalfEdges;
      faceHalfEdges.push(...perim2.faceHalfEdges);
    }

    const faceArrays: FaceArrayViewEntity[] = [];
    const cornerBoardRails: CornerBoardRail[] = [];

    const rtree = new RBush<AABB2>();
    for (let face = 0, faceCount = faceHalfEdges.length; face < faceCount; face++) {
      for (
        let halfEdgeIndex = 0, halfEdgeCount = faceHalfEdges[face].length;
        halfEdgeIndex < halfEdgeCount;
        halfEdgeIndex++
      ) {
        const edge1 = faceHalfEdges[face][halfEdgeIndex];
        const edge2 = faceHalfEdges[face][(halfEdgeIndex + 1) % halfEdgeCount];

        const wall1 = walls[halfEdgeToWall[edge1]];
        const wall2 = walls[halfEdgeToWall[edge2]];

        if (!wall1 || wall1.wallType !== WallType.SOLID || !wall2 || wall2.wallType !== WallType.SOLID) {
          continue;
        }

        const mass1 = solidWallSubstrateLayer(wall1);
        const path1 = getWallPath(wall1);
        const mass2 = solidWallSubstrateLayer(wall2);
        const path2 = getWallPath(wall2);

        const hasExteriorCladding = ({ section: { leftSide, rightSide } }: WallMass) => {
          return leftSide.cladding === WallCladdingType.EXTERIOR || rightSide.cladding === WallCladdingType.EXTERIOR;
        };

        // wall sections are sorted by z so we only need the first exterior section
        // TODO - we probably need a cleaner function for these kinds of things
        const minExtCladding1 = wallMassLayers(wall1.mass, WallLayerType.SUBSTRATE, WallLOD.L2).find(
          hasExteriorCladding,
        );
        const minExtCladding2 = wallMassLayers(wall2.mass, WallLayerType.SUBSTRATE, WallLOD.L2).find(
          hasExteriorCladding,
        );

        const room1 = edge1 === path1.halfEdgeIndex ? path1.leftRoom : path1.rightRoom;
        const room2 = edge2 === path2.halfEdgeIndex ? path2.leftRoom : path2.rightRoom;

        const isExteriorWall1 = !room1 || !isWalledRoomEnclosure[room1.attributes.roomEnclosure];
        const isExteriorWall2 = !room2 || !isWalledRoomEnclosure[room2.attributes.roomEnclosure];

        let exteriorWallOffset1 = edge1 === path1.halfEdgeIndex ? mass1.leftOffset : mass1.rightOffset;
        let exteriorWallOffset2 = edge2 === path2.halfEdgeIndex ? mass2.leftOffset : mass2.rightOffset;

        if (!isExteriorWall1) {
          exteriorWallOffset1 = exteriorWallOffsetWithSheathing;
        }

        if (!isExteriorWall2) {
          exteriorWallOffset2 = exteriorWallOffsetWithSheathing;
        }

        const refRoom1 = edge1 === path1.halfEdgeIndex ? path1.rightRoom : path1.leftRoom;
        const refRoom2 = edge2 === path2.halfEdgeIndex ? path2.rightRoom : path2.leftRoom;
        const entityHandleForSelectingReportingUnit =
          roomEntityHandleForSelectingReportingUnit(refRoom1) ?? roomEntityHandleForSelectingReportingUnit(refRoom2);
        const isHalfStoryEdge1 = !!(refRoom1?.parentId && blocks[refRoom1?.parentId]?.isHalfStory);
        const isHalfStoryEdge2 = !!(refRoom2?.parentId && blocks[refRoom2?.parentId]?.isHalfStory);

        const isHalfStoryCorner = isHalfStoryEdge1 && isHalfStoryEdge2;

        let startZ1 = floorElevation + cornerBoardOffsetForEdge(facades, labels[i], edge1);
        let startZ2 = floorElevation + cornerBoardOffsetForEdge(facades, labels[i], edge2);

        if (minExtCladding1) {
          startZ1 += minExtCladding1.section.bottomOffset;
        }

        if (minExtCladding2) {
          startZ2 += minExtCladding2.section.bottomOffset;
        }

        if (adjustForWainscoting) {
          startZ1 += wainscotingHeight;
          startZ2 += wainscotingHeight;
        }

        const [ax, ay] = planarGraphHalfEdgeStart(planarGraph, edge1);
        const [bx, by] = planarGraphHalfEdgeStart(planarGraph, edge2);
        const [cx, cy] = planarGraphHalfEdgeEnd(planarGraph, edge2);

        const cornerAABB = aabb2Grow(
          aabb2New(bx, by, bx, by),
          CORNER_BOARD_COLLISION_TOLERANCE,
          CORNER_BOARD_COLLISION_TOLERANCE,
        );

        if (rtree.collides(cornerAABB)) {
          continue;
        }

        rtree.insert(cornerAABB);

        const cornerCondition = getCornerCondition(ax, ay, bx, by, cx, cy);
        if (!isValidCornerCondition(cornerCondition)) {
          continue;
        }

        const isInsideCorner = cornerCondition === ValidCornerCondition.INSIDE;
        const dist = isInsideCorner ? insideDist : outsideDist;
        const width = isInsideCorner ? insideWidth : outsideWidth;
        const thickness = isInsideCorner ? insideThickness : outsideThickness;
        const profile = isInsideCorner ? insideProfile : outsideProfile;
        const profileId = isInsideCorner ? insideCornerBoardProfile : outsideCornerBoardProfile;
        const material = isInsideCorner ? insideCornerBoardMaterial : outsideCornerBoardMaterial;
        const viewEntityDescriptor = isInsideCorner ? insideViewEntityDescriptor : outsideViewEntityDescriptor;
        const ceilingHeightOffset1 = path1.leftRoom?.ceilingHeightOffset ?? 0;
        const ceilingHeightOffset2 = path1.rightRoom?.ceilingHeightOffset ?? 0;
        const ceilingHeightOffset3 = path2.leftRoom?.ceilingHeightOffset ?? 0;
        const ceilingHeightOffset4 = path2.rightRoom?.ceilingHeightOffset ?? 0;

        const offsets1 = getOffsetParamsForEdge(exteriorTrimSettings, facades, labels[i], edge1, isInsideCorner);
        const offsets2 = getOffsetParamsForEdge(exteriorTrimSettings, facades, labels[i], edge2, isInsideCorner);
        const endZ =
          floorElevation +
          topOfWall +
          Math.max(ceilingHeightOffset1, ceilingHeightOffset2, ceilingHeightOffset3, ceilingHeightOffset4);
        const baseZ = Math.min(startZ1, startZ2);

        // determine final outside corner board behavior
        const config = cornerBoardConfigForEdge(facades, labels[i], edge1, edge2);

        const resolvedCornerBoards = resolveCornerBoards({
          cornerCondition,
          enableInsideCornerMoulding,
          enableOutsideCornerMoulding,
          config,
        });
        const hasProfileForCorner = hasProfileForCornerCondition(cornerCondition);
        const hasBoardEdge1 = resolvedCornerBoards.hasBoardEdge1 && hasProfileForCorner;
        const hasBoardEdge2 = resolvedCornerBoards.hasBoardEdge2 && hasProfileForCorner;

        // insert the corner board behavior into the rtree
        cornerBoardBehaviorRTree.insert({
          ...cornerAABB,
          ...config,
        });

        if (!hasBoardEdge1 && !hasBoardEdge2) {
          continue;
        }

        const cornerAngle = Math.abs(radianToDegree(vec2AngleBetween(ax - bx, ay - by, cx - bx, cy - by)));
        if (cornerAngle < 1.0 || cornerAngle > 359.0) {
          continue;
        }

        // end points should be outside the roof volume
        if (
          isCornerBoardInsideRoof({
            ax,
            ay,
            bx,
            by,
            cx,
            cy,
            baseZ,
            endZ,
            roofs,
            exteriorWallOffset1,
            exteriorWallOffset2,
          })
        ) {
          continue;
        }

        // special case for half story corners
        if (
          isHalfStoryCorner &&
          !wallHasNonInteriorRoomEnclosure(wall1) &&
          !wallHasNonInteriorRoomEnclosure(wall2) &&
          !isValidHalfStoryCorner({
            ax,
            ay,
            bx,
            by,
            cx,
            cy,
            baseZ,
            endZ,
            roofs,
            exteriorWallOffset1,
            exteriorWallOffset2,
          })
        ) {
          continue;
        }

        const cornerPl = offsetPolyLine2Variable(polyLine2New([ax, ay, bx, by, cx, cy]), [
          -exteriorWallOffset1,
          -exteriorWallOffset2,
        ]);
        const [newX, newY] = polyLine2MiterPt(
          cornerPl[0],
          cornerPl[1],
          cornerPl[2],
          cornerPl[3],
          cornerPl[4],
          cornerPl[5],
          -dist,
        );
        const [castX, castY] = polyLine2MiterPt(
          cornerPl[0],
          cornerPl[1],
          cornerPl[2],
          cornerPl[3],
          cornerPl[4],
          cornerPl[5],
          -thickness,
        );

        const [adjustedStartZ, tmpAdjustedEndZ] = castAgainstRoofs(
          roofFaces,
          floorIndex,
          castX,
          castY,
          baseZ,
          castX,
          castY,
          endZ,
        );

        let adjustedEndZ = tmpAdjustedEndZ;
        if (isInsideCorner && !isPointInsideRoof({ x: castX, y: castY, z: endZ, roofs })) {
          [, adjustedEndZ] = castAgainstRoofs(
            roofFaces,
            0,
            castX,
            castY,
            adjustedStartZ,
            castX,
            castY,
            endZ + Math.max(getMaxPeakHeight(roofs) - endZ, 0),
          );
        }

        if (
          isHalfStoryCorner &&
          isPointBelowRoof({ x: castX, y: castY, z: baseZ, roofs }) &&
          !isPointBelowRoof({ x: castX, y: castY, z: adjustedEndZ, roofs })
        ) {
          [, adjustedEndZ] = castAgainstRoofs(roofFaces, 0, castX, castY, baseZ, castX, castY, adjustedEndZ);
        }

        const adjustedStartZ1 = Math.max(startZ1, adjustedStartZ);
        const adjustedStartZ2 = Math.max(startZ2, adjustedStartZ);

        const refPointOptions = {
          ax,
          ay,
          bx,
          by,
          cx,
          cy,
          wallOffsetEdge1: exteriorWallOffset1,
          wallOffsetEdge2: exteriorWallOffset2,
        };

        const maxStartZ = Math.max(adjustedStartZ1, adjustedStartZ2);
        const instanceId = room1?.instanceId ?? room2?.instanceId ?? refRoom1?.instanceId ?? refRoom2?.instanceId;
        // A corner board inside a component instance is selected as the instance.
        const pickHandle: DocEntityHandle | undefined = instanceId
          ? { type: "COMPONENT_INSTANCE", id: instanceId }
          : undefined;
        const commonProfileOptions = {
          profile,
          profileId,
          material,
          width,
          thickness,
          cornerCondition,
          faceArrays,
          cornerBoardRails,
          viewEntityDescriptor,
          entityHandleForSelectingReportingUnit,
          pickHandle,
          streetOrientationEdge1: getWallStreetOrientation(wall1, perimeterInfo),
          streetOrientationEdge2: getWallStreetOrientation(wall2, perimeterInfo),
        };

        const stackIndex = cornerBoardStacks.length;
        if (maxStartZ < adjustedEndZ) {
          addCornerBoardSegments(
            {
              ...refPointOptions,
              ...commonProfileOptions,
              ...offsets1,
              hasBoardEdge1,
              hasBoardEdge2,
              startZ: maxStartZ,
              endZ: adjustedEndZ,
              floorIndex,
            },
            offsets1,
            offsets2,
            stackIndex,
          );
        }

        if (adjustedStartZ1 < adjustedStartZ2) {
          addCornerBoardSegment(
            {
              ...refPointOptions,
              ...commonProfileOptions,
              ...offsets1,
              hasBoardEdge1,
              hasBoardEdge2: false,
              startZ: adjustedStartZ1,
              endZ: adjustedStartZ2,
              floorIndex,
            },
            stackIndex,
          );
        }

        if (adjustedStartZ2 < adjustedStartZ1) {
          addCornerBoardSegment(
            {
              ...refPointOptions,
              ...commonProfileOptions,
              ...offsets2,
              hasBoardEdge1: false,
              hasBoardEdge2,
              startZ: adjustedStartZ2,
              endZ: adjustedStartZ1,
              floorIndex,
            },
            stackIndex,
          );
        }

        for (let t = 0, lt = underRoofTrim.length; t < lt; t++) {
          if (isUnderRoofTrimInserted[t]) {
            continue;
          }

          const trim = underRoofTrim[t];
          const { trimBase, trimTop, wallPoints } = trim;
          if (
            isCornerBoardInsideRoof({
              ax: wallPoints[0],
              ay: wallPoints[1],
              bx: wallPoints[2],
              by: wallPoints[3],
              cx: wallPoints[4],
              cy: wallPoints[5],
              baseZ: trimBase[2],
              endZ: trimTop[2],
              roofs,
              exteriorWallOffset1: -ROOF_CORNER_BOARD_TOLERANCE,
              exteriorWallOffset2: -ROOF_CORNER_BOARD_TOLERANCE,
            })
          ) {
            continue;
          }

          if (vec2Dist(newX, newY, trimBase[0], trimBase[1]) < ROOF_CORNER_BOARD_TOLERANCE) {
            const resolvedCornerBoards = resolveCornerBoards({
              cornerCondition: trim.cornerCondition,
              enableInsideCornerMoulding,
              enableOutsideCornerMoulding,
              config: mergeCornerBoardConfig(trim.config, config),
            });
            const hasProfileForCorner = hasProfileForCornerCondition(trim.cornerCondition);
            const hasBoardEdge1 = resolvedCornerBoards.hasBoardEdge1 && hasProfileForCorner;
            const hasBoardEdge2 = resolvedCornerBoards.hasBoardEdge2 && hasProfileForCorner;

            addCornerBoardSegments(
              {
                ...commonProfileOptions,
                ax: wallPoints[4],
                ay: wallPoints[5],
                bx: wallPoints[2],
                by: wallPoints[3],
                cx: wallPoints[0],
                cy: wallPoints[1],
                wallOffsetEdge1: 0,
                wallOffsetEdge2: 0,
                hasBoardEdge1,
                hasBoardEdge2,
                startZ: Math.max(endZ, trimBase[2]),
                endZ: trimTop[2],
                floorIndex,
                ...offsets1,
              },
              offsets1,
              offsets2,
              stackIndex,
            );

            isUnderRoofTrimInserted[t] = true;
          }
        }
      }
    }
    const id = uid();
    cornerBoards[id] = cornerBoardsNew(id, level.id, faceArrays, cornerBoardRails);
  }

  if (enableOutsideCornerMoulding) {
    for (let t = 0, lt = underRoofTrim.length; t < lt; t++) {
      if (isUnderRoofTrimInserted[t]) {
        continue;
      }

      const trim = underRoofTrim[t];
      const { trimBase, trimTop, wallPoints, levelId, hasHeelHeightOffset, cornerCondition, wallCenterCorner } = trim;

      if (
        (!enableOutsideCornerMoulding && cornerCondition === ValidCornerCondition.OUTSIDE) ||
        (!enableInsideCornerMoulding && cornerCondition === ValidCornerCondition.INSIDE)
      ) {
        continue;
      }

      if (!hasHeelHeightOffset) {
        continue;
      }

      const faceArrays: FaceArrayViewEntity[] = [];
      const cornerBoardRails: CornerBoardRail[] = [];

      if (
        !isCornerBoardAboveRoof({
          ax: wallPoints[0],
          ay: wallPoints[1],
          bx: wallPoints[2],
          by: wallPoints[3],
          cx: wallPoints[4],
          cy: wallPoints[5],
          baseZ: trimBase[2],
          endZ: trimTop[2],
          roofs,
          exteriorWallOffset1: 0,
          exteriorWallOffset2: 0,
          testWalls: true,
        })
      ) {
        continue;
      }

      // look for the behavior at the corner
      const cornerAABB = aabb2Grow(
        aabb2New(wallCenterCorner[0], wallCenterCorner[1], wallCenterCorner[0], wallCenterCorner[1]),
        CORNER_BOARD_COLLISION_TOLERANCE,
        CORNER_BOARD_COLLISION_TOLERANCE,
      );

      const searchResults = cornerBoardBehaviorRTree.search(cornerAABB);
      const firstResult = searchResults.length > 0 ? searchResults[0] : undefined;

      const resolvedCornerBoards = resolveCornerBoards({
        cornerCondition: trim.cornerCondition,
        enableInsideCornerMoulding,
        enableOutsideCornerMoulding,
        config: mergeCornerBoardConfig(trim.config, firstResult),
      });
      const hasProfileForCorner = hasProfileForCornerCondition(trim.cornerCondition);
      const hasBoardEdge1 = resolvedCornerBoards.hasBoardEdge1 && hasProfileForCorner;
      const hasBoardEdge2 = resolvedCornerBoards.hasBoardEdge2 && hasProfileForCorner;

      if (!hasBoardEdge1 && !hasBoardEdge2) {
        continue;
      }

      const isInsideCorner = cornerCondition === ValidCornerCondition.INSIDE;
      const stackIndex = cornerBoardStacks.length;
      addCornerBoardSegment(
        {
          ax: wallPoints[4],
          ay: wallPoints[5],
          bx: wallPoints[2],
          by: wallPoints[3],
          cx: wallPoints[0],
          cy: wallPoints[1],
          wallOffsetEdge1: 0,
          wallOffsetEdge2: 0,
          hasBoardEdge1,
          hasBoardEdge2,
          profile: isInsideCorner ? insideProfile : outsideProfile,
          profileId: isInsideCorner ? insideCornerBoardProfile : outsideCornerBoardProfile,
          material: isInsideCorner ? insideCornerBoardMaterial : outsideCornerBoardMaterial,
          width: isInsideCorner ? insideWidth : outsideWidth,
          thickness: isInsideCorner ? insideThickness : outsideThickness,
          cornerCondition,
          startZ: trimBase[2],
          endZ: trimTop[2],
          faceArrays,
          cornerBoardRails,
          viewEntityDescriptor: isInsideCorner ? insideViewEntityDescriptor : outsideViewEntityDescriptor,
          floorIndex: 1,
          topAlignment: outsideCornerBoardTopAlignment,
          bottomAlignment: outsideCornerBoardBottomAlignment,
          topOffset: outsideCornerBoardTopOffset,
          bottomOffset: outsideCornerBoardBottomOffset,
        },
        stackIndex,
      );

      const id = uid();
      cornerBoards[id] = cornerBoardsNew(id, levelId, faceArrays, cornerBoardRails);

      isUnderRoofTrimInserted[t] = true;
    }
  }

  cornerBoardStacks.forEach((stack) => {
    const [refX, refY] = getCornerBoardRefPointFromOffsets(
      stack[0],
      2 * MULTIGABLE_BSP_EPSILON,
      2 * MULTIGABLE_BSP_EPSILON,
    );
    const [minOptions1, minOptions2, maxOptions1, maxOptions2, minOptions, maxOptions] = findMinMaxCornerBoards(stack);

    // If the corner boards on edge1 and edge2 have different offsets, we apply the offsets the min or max of that edge.
    // Otherwise, we apply it to the min or max of the whole stack. The difference in behavior is for backwards compatibility.
    // Before January 2025, the edge1 and edge2 offsets were identical and applied to min or max of the whole stack.
    if (minOptions1 && minOptions2 && hasDifferentOffsetsMin(minOptions1, minOptions2)) {
      const minZ1 = applyOffsetZ(minOptions1, true, roofs, levels);
      const minZ2 = applyOffsetZ(minOptions2, true, roofs, levels);
      minOptions1.startZ = minZ1;
      minOptions2.startZ = minZ2;
      stack.forEach((options) => {
        if (options.hasBoardEdge1) {
          options.startZ = Math.max(options.startZ, minZ1);
        }
        if (options.hasBoardEdge2) {
          options.startZ = Math.max(options.startZ, minZ2);
        }
      });
    } else if (minOptions) {
      const minZ = applyOffsetZ(minOptions, true, roofs, levels);
      minOptions.startZ = minZ;
      stack.forEach((options) => {
        options.startZ = Math.max(options.startZ, minZ);
      });
    }

    if (minOptions1 && minOptions2 && maxOptions1 && maxOptions2 && hasDifferentOffsetsMax(maxOptions1, maxOptions2)) {
      const minZ1 = applyOffsetZ(minOptions1, true, roofs, levels);
      const minZ2 = applyOffsetZ(minOptions2, true, roofs, levels);
      const maxZ1 = applyOffsetZ(maxOptions1, false, roofs, levels);
      const maxZ2 = applyOffsetZ(maxOptions2, false, roofs, levels);
      const finalMaxZ1 = clipCornerBoards(refX, refY, clipCornerBoardOptions, minZ1, maxZ1);
      const finalMaxZ2 = clipCornerBoards(refX, refY, clipCornerBoardOptions, minZ2, maxZ2);
      maxOptions1.endZ = finalMaxZ1;
      maxOptions2.endZ = finalMaxZ2;
      stack.forEach((options) => {
        if (options.hasBoardEdge1) {
          options.endZ = Math.min(options.endZ, finalMaxZ1);
        }
        if (options.hasBoardEdge2) {
          options.endZ = Math.min(options.endZ, finalMaxZ2);
        }
      });
    } else if (minOptions && maxOptions) {
      const minZ = applyOffsetZ(minOptions, true, roofs, levels);
      const maxZ = applyOffsetZ(maxOptions, false, roofs, levels);
      const finalMaxZ = clipCornerBoards(refX, refY, clipCornerBoardOptions, minZ, maxZ);
      maxOptions.endZ = finalMaxZ;
      stack.forEach((options) => {
        options.endZ = Math.min(options.endZ, finalMaxZ);
      });
    }
  });

  const mergedStacks = cornerBoardStacks.map(mergeOppositeCornerBoards);

  mergedStacks.forEach((stack) => {
    stack.forEach((options) => {
      buildCornerBoardProfile(options);
    });
  });

  return cornerBoards;
}

// If two corner boards on edge 1 and edge 2 overlap, merge them together. This slightly modifies the geometry
// creating smooth corner between them instead of a seam.
function mergeOppositeCornerBoards(stack: CornerBoardProfileOptions[]): CornerBoardProfileOptions[] {
  // Find the z-values.
  const zValues: number[] = [];
  stack.forEach((options) => {
    // Skip corner boards that include both edges.
    if (options.hasBoardEdge1 && options.hasBoardEdge2) {
      return;
    }
    zValues.push(options.startZ);
    zValues.push(options.endZ);
  });
  zValues.sort((a, b) => a - b);

  // Find the unique z-values.
  const zValuesUnique: number[] = [];
  let prevZ = -Infinity;
  zValues.forEach((z) => {
    if (z > prevZ + DIST_EPSILON) {
      zValuesUnique.push(z);
      prevZ = z;
    }
  });

  // Split the corner boards at the z-values.
  const splitCornerBoards: CornerBoardProfileOptions[] = [];
  stack.forEach((options) => {
    if (options.hasBoardEdge1 && options.hasBoardEdge2) {
      // Corner boards that include both edges should never be split or merged.
      splitCornerBoards.push(options);
      return;
    }

    const splitValues = zValuesUnique.filter(
      (z) => options.startZ + DIST_EPSILON < z && z < options.endZ - DIST_EPSILON,
    );
    splitValues.unshift(options.startZ);
    splitValues.push(options.endZ);

    for (let i = 0; i < splitValues.length - 1; i++) {
      splitCornerBoards.push({ ...options, startZ: splitValues[i], endZ: splitValues[i + 1] });
    }
  });

  // Merge corner boards that have the same z-values but on opposite edges.
  const mergedCornerBoards: CornerBoardProfileOptions[] = [];
  splitCornerBoards.forEach((optionA, indexA) => {
    if (!optionA.hasBoardEdge1 && !optionA.hasBoardEdge2) {
      return;
    }
    for (let indexB = indexA + 1; indexB < splitCornerBoards.length; indexB++) {
      const optionB = splitCornerBoards[indexB];
      if (
        optionA.startZ === optionB.startZ &&
        optionA.endZ === optionB.endZ &&
        optionA.hasBoardEdge1 !== optionB.hasBoardEdge1 &&
        optionA.hasBoardEdge2 !== optionB.hasBoardEdge2
      ) {
        optionA.hasBoardEdge1 = true;
        optionA.hasBoardEdge2 = true;
        optionB.hasBoardEdge1 = false;
        optionB.hasBoardEdge2 = false;
      }
    }
    mergedCornerBoards.push(optionA);
  });

  return mergedCornerBoards;
}

// Get offset params. Use a facade if one is present. Otherwise, fall back on the trim settings.
function getOffsetParamsForEdge(
  exteriorTrimSettings: ExteriorTrimSettings,
  wallFacadeTable: WallFacadeTable,
  labels: HalfEdgeAssociationTable<Label>,
  edge: number,
  isInsideCorner: boolean,
): CornerBoardOffsetParams {
  const {
    outsideCornerBoardBottomAlignment = CornerBoardTrimAlignment.TO_FOUNDATION,
    outsideCornerBoardTopAlignment = CornerBoardTrimAlignment.BO_SOFFIT,
    outsideCornerBoardBottomOffset = 0,
    outsideCornerBoardTopOffset = 0,
    insideCornerBoardBottomAlignment = CornerBoardTrimAlignment.TO_FOUNDATION,
    insideCornerBoardTopAlignment = CornerBoardTrimAlignment.BO_SOFFIT,
    insideCornerBoardBottomOffset = 0,
    insideCornerBoardTopOffset = 0,
  } = exteriorTrimSettings;
  let topAlignment = isInsideCorner ? insideCornerBoardTopAlignment : outsideCornerBoardTopAlignment;
  let bottomAlignment = isInsideCorner ? insideCornerBoardBottomAlignment : outsideCornerBoardBottomAlignment;
  let topOffset = isInsideCorner ? insideCornerBoardTopOffset : outsideCornerBoardTopOffset;
  let bottomOffset = isInsideCorner ? insideCornerBoardBottomOffset : outsideCornerBoardBottomOffset;

  iterateFacadesForHalfEdge(wallFacadeTable, labels, edge, (facade) => {
    if (facade) {
      bottomAlignment =
        (isInsideCorner ? facade.insideCornerBoardBottomAlignment : facade.outsideCornerBoardBottomAlignment) ??
        bottomAlignment;
      bottomOffset =
        (isInsideCorner ? facade.insideCornerBoardBottomOffset : facade.outsideCornerBoardBottomOffset) ?? bottomOffset;
      topAlignment =
        (isInsideCorner ? facade.insideCornerBoardTopAlignment : facade.outsideCornerBoardTopAlignment) ?? topAlignment;
      topOffset =
        (isInsideCorner ? facade.insideCornerBoardTopOffset : facade.outsideCornerBoardTopOffset) ?? topOffset;
      return true;
    }
    return false; // do not return early
  });
  return { topAlignment, topOffset, bottomAlignment, bottomOffset };
}

// Find the options with the min and max z-value for edge1 and edge2.
function findMinMaxPartial(stack: CornerBoardProfileOptions[]): (CornerBoardProfileOptions | null)[] {
  let minOptions1: CornerBoardProfileOptions | null = null;
  let minOptions2: CornerBoardProfileOptions | null = null;
  let maxOptions1: CornerBoardProfileOptions | null = null;
  let maxOptions2: CornerBoardProfileOptions | null = null;

  stack.forEach((options) => {
    if (options.hasBoardEdge1) {
      if (!minOptions1 || options.startZ < minOptions1.startZ) {
        minOptions1 = options;
      }
      if (!maxOptions1 || options.endZ > maxOptions1.endZ) {
        maxOptions1 = options;
      }
    }
    if (options.hasBoardEdge2) {
      if (!minOptions2 || options.startZ < minOptions2.startZ) {
        minOptions2 = options;
      }
      if (!maxOptions2 || options.endZ > maxOptions2.endZ) {
        maxOptions2 = options;
      }
    }
  });

  return [minOptions1, minOptions2, maxOptions1, maxOptions2];
}

// Find the options with the min and max z-value for edge1 and edge2.
function findMinMaxCornerBoards(stack: CornerBoardProfileOptions[]): (CornerBoardProfileOptions | null)[] {
  const [minOptions1, minOptions2, maxOptions1, maxOptions2] = findMinMaxPartial(stack);
  let minOptions: CornerBoardProfileOptions | null = null;
  if (minOptions1 && minOptions2) {
    minOptions = minOptions1.startZ < minOptions2.startZ ? minOptions1 : minOptions2;
  } else {
    minOptions = minOptions1 || minOptions2;
  }
  let maxOptions: CornerBoardProfileOptions | null = null;
  if (maxOptions1 && maxOptions2) {
    maxOptions = maxOptions1.endZ > maxOptions2.endZ ? maxOptions1 : maxOptions2;
  } else {
    maxOptions = maxOptions1 || maxOptions2;
  }

  return [minOptions1, minOptions2, maxOptions1, maxOptions2, minOptions, maxOptions];
}

// Check if the options has the same bottom offsets and alignments.
function hasDifferentOffsetsMin(options1: CornerBoardOffsetParams, options2: CornerBoardOffsetParams): boolean {
  return options1.bottomOffset !== options2.bottomOffset || options1.bottomAlignment !== options2.bottomAlignment;
}

// Check if the options has the same top offsets and alignments.
function hasDifferentOffsetsMax(options1: CornerBoardOffsetParams, options2: CornerBoardOffsetParams): boolean {
  return options1.topOffset !== options2.topOffset || options1.topAlignment !== options2.topAlignment;
}

// Check if the options has the same bottom offsets and alignments.
function hasDifferentOffsets(options1: CornerBoardOffsetParams, options2: CornerBoardOffsetParams): boolean {
  return hasDifferentOffsetsMin(options1, options2) || hasDifferentOffsetsMax(options1, options2);
}

function clipCornerBoards(
  refX: number,
  refY: number,
  clipCornerBoardOptions: ClipRailContext,
  minZ: number,
  maxZ: number,
): number {
  // clip the rail
  const clippedRails = clipRails3({
    rails: [polyLine3ByTwoPoints(refX, refY, minZ, refX, refY, maxZ)],
    options: clipCornerBoardOptions,
  });

  // look for the top most point of the clipped rails
  if (clippedRails.length > 0) {
    const [, , clippedZ] = polyLine3EndPoint(clippedRails[clippedRails.length - 1]);

    if (clippedZ < maxZ - DIST_EPSILON) {
      maxZ = clippedZ - GABLE_ROOF_THICKNESS;
    }
  }
  return maxZ;
}

function cornerBoardOffsetForEdge(
  wallFacadeTable: WallFacadeTable,
  labels: HalfEdgeAssociationTable<Label>,
  edge: number,
): number {
  let offset: number | null = null;

  iterateFacadesForHalfEdge(
    wallFacadeTable,
    labels,
    edge,
    (facade) => {
      if (facade?.cornerBoardOffsetZ) {
        offset = Math.max(offset ?? Number.NEGATIVE_INFINITY, facade.cornerBoardOffsetZ);
      }
      return false; // do not return early
    },
    () => {
      return offset !== null;
    },
  );

  return offset ?? 0;
}

function getMaxPeakHeight(roofs: Table<GeneratedRoofSystem>): number {
  let maxPeakHeight = 0;
  for (const roofId in roofs) {
    const roof = roofs[roofId];

    const { roofs: subRoofs } = roof;
    for (const subRoofId in subRoofs) {
      const { peakHeight } = subRoofs[subRoofId].roofVolume;
      maxPeakHeight = Math.max(maxPeakHeight, peakHeight);
    }
  }

  return maxPeakHeight;
}

function collectUnderRoofTrim(roofs: Table<GeneratedRoofSystem>): UnderRoofTrim[] {
  const ret: UnderRoofTrim[] = [];
  for (const roofId in roofs) {
    const roof = roofs[roofId];

    const { roofs: subRoofs } = roof;
    for (const subRoofId in subRoofs) {
      const { wallFaces } = subRoofs[subRoofId];
      ret.push(...wallFaces.underRoofTrim);
    }
  }
  return ret;
}

function getSoffitBottom(
  x: number,
  y: number,
  floorIndex: number,
  roofs: Table<GeneratedRoofSystem>,
  levels: Table<Level>,
  alignment: CornerBoardTrimAlignment,
): number {
  let soffitBottom = Number.NEGATIVE_INFINITY;
  let isValidBottom = false;

  const aabb = aabb2New(
    x - ROOF_CORNER_BOARD_TOLERANCE,
    y - ROOF_CORNER_BOARD_TOLERANCE,
    x + ROOF_CORNER_BOARD_TOLERANCE,
    y + ROOF_CORNER_BOARD_TOLERANCE,
  );

  for (const roofId in roofs) {
    const roof = roofs[roofId];

    const { roofs: subRoofs } = roof;
    for (const subRoofId in subRoofs) {
      const { wallFaces } = subRoofs[subRoofId];
      const { datumsAtCorner } = wallFaces;

      const collisions = datumsAtCorner.search(aabb);
      for (const { bottomOfSoffit, bottomOfFriezeBoard, levelId } of collisions) {
        const { floorIndex: roofFloorIndex } = levels[levelId];

        if (roofFloorIndex >= floorIndex) {
          soffitBottom = Math.max(
            soffitBottom,
            alignment === CornerBoardTrimAlignment.BO_FRIEZE_BOARD ? bottomOfFriezeBoard : bottomOfSoffit,
          );
          isValidBottom = true;
        }
      }
    }
  }
  return isValidBottom ? soffitBottom : Infinity;
}

function applyOffsetZ(
  options: CornerBoardProfileOptions,
  isStart: boolean,
  roofs: Table<GeneratedRoofSystem>,
  levels: Table<Level>,
): number {
  const [x, y] = getCornerBoardRefPoint(options);
  const { startZ, endZ, floorIndex, bottomAlignment, bottomOffset, topAlignment, topOffset } = options;
  const alignment = isStart ? bottomAlignment : topAlignment;
  const offset = isStart ? bottomOffset : topOffset;
  const z = isStart ? startZ : endZ;
  let baselineZ = 0;
  switch (alignment) {
    case CornerBoardTrimAlignment.TO_FOUNDATION:
      baselineZ = startZ;
      break;
    case CornerBoardTrimAlignment.BO_SOFFIT:
    case CornerBoardTrimAlignment.BO_SOFFIT_LEGACY:
    case CornerBoardTrimAlignment.BO_FRIEZE_BOARD: {
      const soffitBottom = getSoffitBottom(x, y, floorIndex, roofs, levels, alignment);
      baselineZ = Math.min(soffitBottom, z);
      break;
    }
  }
  if (isStart) {
    return baselineZ + offset;
  } else {
    switch (alignment) {
      case CornerBoardTrimAlignment.TO_FOUNDATION:
        return floorIndex === 0 ? baselineZ + offset : offset;
      case CornerBoardTrimAlignment.BO_SOFFIT:
      case CornerBoardTrimAlignment.BO_FRIEZE_BOARD:
      case CornerBoardTrimAlignment.BO_SOFFIT_LEGACY:
        return baselineZ + offset;
      default:
        assertUnreachable(alignment);
    }
  }
}

interface FaceWithBaseHeight {
  face: Face;
  baseHeight: number;
}

function faceWithBaseHeightNew(face: Face, baseHeight: number): FaceWithBaseHeight {
  return {
    face,
    baseHeight,
  };
}

function collectRoofFaces(roofSystems: Table<GeneratedRoofSystem>): FaceWithBaseHeight[] {
  const roofFaces: FaceWithBaseHeight[] = [];

  const collectRoofSystemFaces = (roofSystem: GeneratedRoofSystem) => {
    if (!roofSystem) {
      return;
    }

    const { roofs: subRoofs } = roofSystem;

    for (const subRoofId in subRoofs) {
      const { roofTopFaces, roofVolume, roofSettings } = subRoofs[subRoofId];
      const { baseHeight, heelHeightOffsets } = roofVolume;

      for (const { face, side } of roofTopFaces) {
        let faceBaseHeight = baseHeight - getEaveDepth(roofSettings);
        if (side === RoofSide.LEFT) {
          faceBaseHeight += heelHeightOffsets.leftOffset;
        } else if (side === RoofSide.RIGHT) {
          faceBaseHeight += heelHeightOffsets.rightOffset;
        }

        roofFaces.push(faceWithBaseHeightNew(face, faceBaseHeight));
      }
    }
  };

  for (const roofId in roofSystems) {
    collectRoofSystemFaces(roofSystems[roofId]);
  }

  return roofFaces;
}

function castAgainstRoofs(
  faces: FaceWithBaseHeight[],
  floorIndex: number,
  x1: number,
  y1: number,
  z1: number,
  x2: number,
  y2: number,
  z2: number,
): [number, number] {
  const midZ = (z1 + z2) / 2;

  for (const { face, baseHeight } of faces) {
    const [nx, ny, nz] = planeNormal(face.plane);
    if (vec3Dot(nx, ny, nz, 0, 0, 1) < ZERO_EPSILON) {
      continue;
    }
    const d = vec3Dot(face.plane.originX, face.plane.originY, face.plane.originZ, nx, ny, nz);
    const [abX, abY, abZ] = vec3Sub(x2, y2, z2, x1, y1, z1);
    const t = (d - vec3Dot(nx, ny, nz, x1, y1, z1)) / vec3Dot(nx, ny, nz, abX, abY, abZ);
    if (t >= DIST_EPSILON && t <= ONE_MINUS_DIST_EPSILON) {
      const [px, py, pz] = vec3Lerp(x1, y1, z1, x2, y2, z2, t);
      const [u, v] = planeProject(face.plane, px, py, pz);
      if (classifyContainmentVec2PolyLine2(u, v, face.loops[0]) === Vec2PolyLine2ContainmentClass.INSIDE) {
        if (floorIndex > 0 && baseHeight < midZ + DIST_EPSILON) {
          z1 = Math.max(pz, z1);
        } else {
          z2 = Math.min(pz - ROOF_THICKNESS, z2);
        }
      }
    }
  }

  return [z1, z2];
}

interface IsValidHalfStoryCornerOptions {
  ax: number;
  ay: number;
  bx: number;
  by: number;
  cx: number;
  cy: number;
  baseZ: number;
  endZ: number;
  roofs: Table<GeneratedRoofSystem>;
  exteriorWallOffset1: number;
  exteriorWallOffset2: number;
}

function isValidHalfStoryCorner({
  ax,
  ay,
  bx,
  by,
  cx,
  cy,
  baseZ,
  endZ,
  roofs,
  exteriorWallOffset1,
  exteriorWallOffset2,
}: IsValidHalfStoryCornerOptions): boolean {
  const [t0x, t0y] = line2Tangent(bx, by, ax, ay);
  const [t1x, t1y] = line2Tangent(bx, by, cx, cy);

  const cornerLine = offsetPolyLine2Variable(polyLine2New([bx + t0x, by + t0y, bx, by, bx + t1x, by + t1y]), [
    -exteriorWallOffset1,
    -exteriorWallOffset2,
  ]);
  const [x0, y0] = polyLine2Start(cornerLine);
  const [x1, y1] = polyLine2End(cornerLine);

  let rails1: PolyLine3[] = [polyLine3ByTwoPoints(x0, y0, baseZ, x0, y0, endZ)];
  let rails2: PolyLine3[] = [polyLine3ByTwoPoints(x1, y1, baseZ, x1, y1, endZ)];

  for (const roofId in roofs) {
    const roof = roofs[roofId];

    const { insideBSP } = roof.clipContext;

    // Clip both rails using the reverse of the inside BSP
    // This will leave us with the rails that are inside or coplanar to the roof volume
    const options: ClipRailContext = {
      clippingNodes: [clippingNodeNew(insideBSP, true)],
      coplanarsToFront: false,
      epsilon: MULTIGABLE_BSP_EPSILON,
    };

    rails1 = clipRails3({
      rails: rails1,
      options,
    });

    rails2 = clipRails3({
      rails: rails2,
      options,
    });

    // Clip the resulting rails (inside or coplanar with volume) with
    // the opposite test as before
    // This will leave us only with the rails that are coplanar to the volume
    const exteriorOptions: ClipRailContext = {
      clippingNodes: [clippingNodeNew(insideBSP)],
      coplanarsToFront: true,
      epsilon: MULTIGABLE_BSP_EPSILON,
    };

    rails1 = clipRails3({
      rails: rails1,
      options: exteriorOptions,
    });

    rails2 = clipRails3({
      rails: rails2,
      options: exteriorOptions,
    });

    // If one or both of the corner rails is empty, it means that the corner board
    // is fully or partially inside the roof and it is not valid for this roof system
    if (rails1.length === 0 || rails2.length === 0) {
      continue;
    }

    return true;
  }

  // if we reach this point and there are no roofs, then the corner board is
  // considered valid
  return Object.keys(roofs).length === 0;
}

interface IsCornerBoardInsideRoofOptions {
  ax: number;
  ay: number;
  bx: number;
  by: number;
  cx: number;
  cy: number;
  baseZ: number;
  endZ: number;
  roofs: Table<GeneratedRoofSystem>;
  exteriorWallOffset1: number;
  exteriorWallOffset2: number;
  testWalls?: boolean;
}

function isCornerBoardInsideRoof({
  ax,
  ay,
  bx,
  by,
  cx,
  cy,
  baseZ,
  endZ,
  roofs,
  exteriorWallOffset1,
  exteriorWallOffset2,
  testWalls,
}: IsCornerBoardInsideRoofOptions): boolean {
  const samplePoints = [0.0, 0.25, 0.5, 0.75, 1.0];
  const samplePointCount = samplePoints.length;

  const [t0x, t0y] = line2Tangent(bx, by, ax, ay);
  const [t1x, t1y] = line2Tangent(bx, by, cx, cy);

  const cornerLine = offsetPolyLine2Variable(polyLine2New([bx + t0x, by + t0y, bx, by, bx + t1x, by + t1y]), [
    -exteriorWallOffset1,
    -exteriorWallOffset2,
  ]);
  const [x0, y0] = polyLine2Start(cornerLine);
  const [x1, y1] = polyLine2End(cornerLine);

  for (const roofId in roofs) {
    const roof = roofs[roofId];

    const { roofsBSP, insideBSP } = roof.clipContext;

    let isInsideRoof = true;
    for (let i = 0; i < samplePointCount; i++) {
      const z = baseZ + (endZ - baseZ) * samplePoints[i];

      if (
        !(isPointInsideBSP(roofsBSP, x0, y0, z, true) || (testWalls && isPointInsideBSP(insideBSP, x0, y0, z, true))) &&
        !(isPointInsideBSP(roofsBSP, x1, y1, z, true) || (testWalls && isPointInsideBSP(insideBSP, x1, y1, z, true)))
      ) {
        isInsideRoof = false;
        break;
      }
    }

    if (isInsideRoof) {
      return true;
    }
  }

  return false;
}

interface IsPointBelowRoof {
  x: number;
  y: number;
  z: number;
  roofs: Table<GeneratedRoofSystem>;
}

function isPointBelowRoof({ x, y, z, roofs }: IsPointBelowRoof): boolean {
  for (const roofId in roofs) {
    const roof = roofs[roofId];

    const { discardBSP } = roof.clipContext;

    if (isPointInsideBSP(discardBSP, x, y, z, true)) {
      return true;
    }
  }

  return false;
}

interface IsPointInsideRoof {
  x: number;
  y: number;
  z: number;
  roofs: Table<GeneratedRoofSystem>;
}

function isPointInsideRoof({ x, y, z, roofs }: IsPointInsideRoof): boolean {
  for (const roofId in roofs) {
    const roof = roofs[roofId];

    const { insideBSP } = roof.clipContext;

    if (isPointInsideBSP(insideBSP, x, y, z, true)) {
      return true;
    }
  }

  return false;
}

function isCornerBoardAboveRoof(options: IsCornerBoardInsideRoofOptions): boolean {
  if (isCornerBoardInsideRoof(options)) {
    return false;
  }

  const { ax, ay, bx, by, cx, cy, baseZ, roofs, exteriorWallOffset1, exteriorWallOffset2 } = options;

  const [t0x, t0y] = line2Tangent(bx, by, ax, ay);
  const [t1x, t1y] = line2Tangent(bx, by, cx, cy);

  const cornerLine = offsetPolyLine2Variable(polyLine2New([bx + t0x, by + t0y, bx, by, bx + t1x, by + t1y]), [
    -exteriorWallOffset1,
    -exteriorWallOffset2,
  ]);
  const [x0, y0] = polyLine2Start(cornerLine);
  const [x1, y1] = polyLine2End(cornerLine);

  for (const roofId in roofs) {
    const roof = roofs[roofId];

    const { roofsBSP } = roof.clipContext;

    if (isPointInsideBSP(roofsBSP, x0, y0, baseZ, true) && isPointInsideBSP(roofsBSP, x1, y1, baseZ, true)) {
      return true;
    }
  }

  return false;
}
